import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Download } from 'lucide-react';
import { Container, Button } from '../styles/GlobalStyles';
import { theme } from '../styles/theme';
import { useAppContext } from '../context/AppContext';
import FinalResult from '../components/FinalResult';
import { TenderAnalysisAPI } from '../services/api';
import { TenderAnalysisState } from '../types';

const ResultsContainer = styled.div`
  min-height: 80vh;
  padding: ${theme.spacing[8]} 0;
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[8]};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${theme.spacing[4]};
    text-align: center;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const ResultsTitle = styled.h1`
  margin-bottom: ${theme.spacing[2]};
  color: ${theme.colors.textPrimary};
`;

const ResultsSubtitle = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.lg};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};

  @media (max-width: ${theme.breakpoints.sm}) {
    width: 100%;
    justify-content: center;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${theme.spacing[16]} 0;
  color: ${theme.colors.textSecondary};
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error}10;
  border: 1px solid ${theme.colors.error}30;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[8]};
  color: ${theme.colors.error};
  text-align: center;
`;

const ResultsPage: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const { 
    state, 
    setLoading, 
    setError, 
    setAnalysisData, 
    setCurrentStep,
    resetState 
  } = useAppContext();

  useEffect(() => {
    if (!threadId) {
      navigate('/');
      return;
    }

    setCurrentStep('results');
    loadAnalysisResults();
  }, [threadId]);

  const loadAnalysisResults = async () => {
    if (!threadId) return;

    try {
      setLoading(true);
      setError(null);

      // In a real implementation, fetch the actual results
      // const response = await TenderAnalysisAPI.getAnalysisStatus(threadId);
      
      // For demo purposes, create mock analysis data
      const mockAnalysisData: TenderAnalysisState = {
        url: state.formData.url || 'https://tender.com/tender.pdf',
        successful_tender_urls: state.formData.successful_tender_urls || [],
        company_id: state.formData.company_id || 'demo-company',
        url_processing_result: 'Successfully processed document',
        initial_review_result: {
          score: 78,
          review: 'Your tender response demonstrates good understanding of requirements but could benefit from more detailed technical specifications and stronger evidence of past experience. The cost breakdown is comprehensive but lacks some granular details that would enhance transparency.',
        },
        questions: [
          {
            id: '1',
            text: 'Describe your company\'s experience with similar projects in the last 5 years.',
            type: 'experience',
            section: 'Company Capability',
            weight: 25,
          },
          {
            id: '2',
            text: 'Provide a detailed technical approach for implementing the solution.',
            type: 'technical',
            section: 'Technical Approach',
            weight: 30,
          },
          {
            id: '3',
            text: 'What is your proposed timeline and key milestones?',
            type: 'project_management',
            section: 'Project Plan',
            weight: 20,
          },
          {
            id: '4',
            text: 'Provide a comprehensive cost breakdown including all phases.',
            type: 'financial',
            section: 'Cost Proposal',
            weight: 25,
          },
        ],
        evaluation_criteria: [
          {
            id: '1',
            name: 'Technical Capability',
            description: 'Demonstration of technical expertise and solution quality',
            weight: 35,
            scoring_method: 'Weighted scoring',
          },
          {
            id: '2',
            name: 'Experience and Track Record',
            description: 'Relevant experience and past performance',
            weight: 25,
            scoring_method: 'Threshold scoring',
          },
          {
            id: '3',
            name: 'Cost Effectiveness',
            description: 'Value for money and cost competitiveness',
            weight: 25,
            scoring_method: 'Price evaluation',
          },
          {
            id: '4',
            name: 'Project Management',
            description: 'Project delivery approach and timeline',
            weight: 15,
            scoring_method: 'Qualitative assessment',
          },
        ],
        requirements_summary: 'The tender requires implementation of a comprehensive digital transformation solution with focus on cloud migration, data analytics, and user experience improvements.',
        final_report: `# Tender Analysis Report

## Executive Summary
This tender analysis has identified key areas for improvement in your response to increase competitiveness and win probability.

## Key Findings
- 15 questions identified across 4 main sections
- 8 evaluation criteria with varying weights
- Strong technical foundation but needs enhancement in experience section
- Cost proposal is competitive but requires more detail

## Recommendations
1. Strengthen technical specifications with more detailed architecture diagrams
2. Include 2-3 additional case studies from similar projects
3. Provide more granular cost breakdown by work package
4. Add risk mitigation strategies

## Score Breakdown
- Technical Capability: 82/100
- Experience: 71/100  
- Cost Effectiveness: 85/100
- Project Management: 74/100

Overall Score: 78/100`,
        comparison_content: 'Detailed comparison content would be available here showing before/after analysis.',
      };

      setAnalysisData(mockAnalysisData);

    } catch (error) {
      console.error('Error loading analysis results:', error);
      setError('Failed to load analysis results. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = () => {
    if (!state.analysisData?.final_report) return;

    const blob = new Blob([state.analysisData.final_report], { 
      type: 'text/markdown' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tender-analysis-${threadId}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleStartNewAnalysis = () => {
    resetState();
    navigate('/');
  };

  const handleBackToAnalysis = () => {
    if (threadId) {
      navigate(`/analysis/${threadId}`);
    }
  };

  if (state.isLoading) {
    return (
      <ResultsContainer>
        <Container>
          <LoadingMessage>
            Loading analysis results...
          </LoadingMessage>
        </Container>
      </ResultsContainer>
    );
  }

  if (state.error) {
    return (
      <ResultsContainer>
        <Container>
          <ErrorMessage>
            {state.error}
          </ErrorMessage>
          <div style={{ textAlign: 'center' }}>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </div>
        </Container>
      </ResultsContainer>
    );
  }

  if (!state.analysisData) {
    return (
      <ResultsContainer>
        <Container>
          <ErrorMessage>
            No analysis data available. Please start a new analysis.
          </ErrorMessage>
          <div style={{ textAlign: 'center' }}>
            <Button onClick={handleStartNewAnalysis}>
              Start New Analysis
            </Button>
          </div>
        </Container>
      </ResultsContainer>
    );
  }

  return (
    <ResultsContainer>
      <Container>
        <ResultsHeader>
          <HeaderContent>
            <ResultsTitle>Analysis Complete</ResultsTitle>
            <ResultsSubtitle>
              Your tender document has been analyzed successfully. 
              Review the results and recommendations below.
            </ResultsSubtitle>
          </HeaderContent>
          <HeaderActions>
            <Button $variant="ghost" onClick={handleBackToAnalysis}>
              <ArrowLeft size={20} />
              Back to Analysis
            </Button>
            <Button $variant="outline" onClick={handleDownloadReport}>
              <Download size={20} />
              Export Report
            </Button>
          </HeaderActions>
        </ResultsHeader>

        <FinalResult
          analysisData={state.analysisData}
          onDownloadReport={handleDownloadReport}
          onStartNewAnalysis={handleStartNewAnalysis}
        />
      </Container>
    </ResultsContainer>
  );
};

export default ResultsPage;